/* This Source Code Form is subject to the terms of the Mozilla Public
 * License, v. 2.0. If a copy of the MPL was not distributed with this
 * file, You can obtain one at http://mozilla.org/MPL/2.0/. */

import { SymbolsNotFoundError } from './errors';

import type { RequestedLib } from 'firefox-profiler/types';
import type { SymbolTableAsTuple } from './symbol-store-db';
import { ensureExists } from '../utils/types';

import { bisectionRight } from 'firefox-profiler/utils/bisect';

export type LibSymbolicationRequest = {
  lib: RequestedLib;
  addresses: Set<number>;
};

export type LibSymbolicationResponse =
  | {
      type: 'SUCCESS';
      lib: RequestedLib;
      results: Map<number, AddressResult>;
    }
  | {
      type: 'ERROR';
      request: LibSymbolicationRequest;
      error: Error;
    };

export type AddressResult = {
  // The name of the outer function that this address belongs to.
  name: string;
  // The address (relative to the library) where the function that
  // contains this address starts, i.e. the address of the function symbol.
  symbolAddress: number;
  // The path of the file that contains the source code of the outer function that contains
  // this address.
  // Optional because the information may not be known by the symbolication source, or because
  // the symbolication method does not expose it.
  file?: string;
  // The line number that contains the source code of the outer function that generated the
  // instructions at the address, optional.
  // Optional because the information may not be known by the symbolication source, or because
  // the symbolication method does not expose it.
  line?: number;
  // An optional inline callstack, ordered from inside to outside.
  // addressResult.name calls addressResult.inlines[inlines.length - 1].function, which
  // calls addressResult.inlines[inlines.length - 2].function etc.
  inlines?: Array<AddressInlineFrame>;
  // An optional size, in bytes, of the machine code of the outer function that
  // this address belongs to.
  functionSize?: number;
};

export type AddressInlineFrame = {
  name: string;
  file?: string;
  line?: number;
};

export interface SymbolProvider {
  // Cheap, should be called first.
  requestSymbolsFromServer(
    requests: LibSymbolicationRequest[]
  ): Promise<LibSymbolicationResponse[]>;

  // Expensive, should be called if requestSymbolsFromServer was unsuccessful.
  requestSymbolsFromBrowser(
    requests: LibSymbolicationRequest[]
  ): Promise<LibSymbolicationResponse[]>;

  // Expensive, should be called if the other two options were unsuccessful.
  // Does not carry file + line + inlines information.
  requestSymbolsViaSymbolTableFromBrowser(
    request: LibSymbolicationRequest,
    ignoreCache: boolean
  ): Promise<Map<number, AddressResult>>;
}

export interface AbstractSymbolStore {
  getSymbols(
    requests: LibSymbolicationRequest[],
    successCb: (lib: RequestedLib, results: Map<number, AddressResult>) => void,
    errorCb: (request: LibSymbolicationRequest, error: Error) => void,
    ignoreCache?: boolean
  ): Promise<void>;
}

// The Mozilla symbolication API has a limit of 10 jobs per request, so limit
// the number of libraries we request in each chunk to that same number.
const MAX_JOB_COUNT_PER_CHUNK = 10;

// Look up the symbols for the given addresses in the symbol table.
// The symbol table is given in the [addrs, index, buffer] format.
// This format is documented at the SymbolTableAsTuple flow type definition.
export function readSymbolsFromSymbolTable(
  addresses: Set<number>,
  symbolTable: SymbolTableAsTuple,
  demangleCallback: (name: string) => string
): Map<number, AddressResult> {
  const [symbolTableAddrs, symbolTableIndex, symbolTableBuffer] = symbolTable;
  const addressArray = Uint32Array.from(addresses);
  addressArray.sort();

  // Iterate over all addresses in addressArray and look them up in the
  // symbolTableAddrs array. The index at which a match is found can be used
  // to obtain the start and end position of its string in the buffer, using
  // the symbolTableIndex array.
  // Both addressArray and symbolTableAddrs are sorted in ascending order.
  const decoder = new TextDecoder();
  const results = new Map<number, AddressResult>();
  let currentSymbolIndex = undefined;
  let currentSymbol = '';
  let currentSymbolFunctionSize = undefined;
  for (let i = 0; i < addressArray.length; i++) {
    const address = addressArray[i];

    // Look up address in symbolTableAddrs. symbolTableAddrs is sorted, so we
    // can do the lookup using bisection. And address is >= the previously
    // looked up address, so we can use the last found index as a lower bound
    // during the bisection.
    // We're not looking for an exact match here. We're looking for the
    // largest symbolIndex for which symbolTableAddrs[symbolIndex] <= address.
    // bisection() returns the insertion index, which is one position after
    // the index that we consider the match, so we need to subtract 1 from the
    // result.
    const symbolIndex: number =
      bisectionRight(symbolTableAddrs, address, currentSymbolIndex) - 1;

    if (symbolIndex >= 0) {
      if (symbolIndex !== currentSymbolIndex) {
        // Get the corresponding string from symbolTableBuffer. The start and
        // end positions are recorded in symbolTableIndex.
        const startOffset = symbolTableIndex[symbolIndex];
        const endOffset = symbolTableIndex[symbolIndex + 1];
        const subarray = symbolTableBuffer.subarray(startOffset, endOffset);
        // C++ or rust symbols in the symbol table may have mangled names.
        // Demangle them here.
        currentSymbol = demangleCallback(decoder.decode(subarray));
        currentSymbolFunctionSize =
          symbolIndex < symbolTableAddrs.length - 1
            ? symbolTableAddrs[symbolIndex + 1] - symbolTableAddrs[symbolIndex]
            : undefined;
        currentSymbolIndex = symbolIndex;
      }
      results.set(address, {
        symbolAddress: symbolTableAddrs[symbolIndex],
        name: currentSymbol,
        functionSize: currentSymbolFunctionSize,
      });
    } else {
      results.set(address, {
        symbolAddress: 0,
        name: '<before first symbol>',
      });
    }
  }
  return results;
}

// Partition the array into "chunks".
// Every element in the array is assigned a numeric value using the computeValue
// callback function. The chunks are chosen in such a way that the accumulated
// value in each chunk does not exceed maxValue, if possible.
// In other words, for each chunk, the following will hold:
// sum(chunk.map(computeValue)) <= maxValue or chunk.length == 1.
// The array is allowed to contain elements which are larger than the maximum
// value on their own; such elements will get a single chunk for themselves.
// Additionally, each chunk is limited to contain no more than maxChunkLength
// elements.
function _partitionIntoChunksOfMaxValue<T>(
  array: T[],
  maxValue: number,
  maxChunkLength: number,
  computeValue: (element: T) => number
): T[][] {
  const chunks: Array<{ value: number; elements: T[] }> = [];
  for (const element of array) {
    const elementValue = computeValue(element);
    // Find an existing chunk that still has enough "value space" left to
    // accomodate this element.
    let chunk = chunks.find(
      ({ value, elements }) =>
        value + elementValue <= maxValue &&
        elements.length + 1 <= maxChunkLength
    );
    if (chunk === undefined) {
      // If no chunk was found, create a new chunk.
      chunk = { value: 0, elements: [] };
      chunks.push(chunk);
    }
    chunk.elements.push(element);
    chunk.value += elementValue;
  }
  return chunks.map(({ elements }) => elements);
}

/**
 * The SymbolStore implements efficient lookup of symbols for a set of addresses.
 * It consults multiple sources of symbol information and caches some results.
 * It only implements one public method: getSymbols.
 * @class SymbolStore
 */
export class SymbolStore {
  _symbolProvider: SymbolProvider;

  constructor(symbolProvider: SymbolProvider) {
    this._symbolProvider = symbolProvider;
  }

  /**
   * Look up symbols for the given addresses in the given libraries.
   * For each LibSymbolicationRequest in requests, either errorCb or successCb
   * will be called.
   * This method returns a promise that resolves when the callbacks for all
   * requests have been called.
   */
  async getSymbols(
    requests: LibSymbolicationRequest[],
    successCb: (lib: RequestedLib, results: Map<number, AddressResult>) => void,
    errorCb: (request: LibSymbolicationRequest, error: Error) => void,
    ignoreCache: boolean = false
  ): Promise<void> {
    // For each library, we have three options to obtain symbol information for
    // it. We try all options in order, advancing to the next option on failure.
    // Option 1: Obtain symbols from the symbol server, using the symbolication API.
    // Option 2: Obtain symbols from the browser, using the symbolication API.
    // Option 3: Obtain symbols from the browser, via individual symbol tables.
    //
    // Symbol tables provide less information than the symbolication API (no inlines
    // and no file / line information) so we try the API before we check for cached
    // symbol tables.

    // Check requests for validity first.
    requests = requests.filter((request) => {
      const { debugName, breakpadId } = request.lib;
      if (debugName === '' || breakpadId === '') {
        errorCb(
          request,
          new SymbolsNotFoundError(
            `Failed to symbolicate library ${debugName}/${breakpadId}`,
            request.lib,
            new Error('Invalid debugName or breakpadId')
          )
        );
        return false;
      }
      return true;
    });

    // Option 1:
    // Try to service requests using the symbolication API,
    // requesting chunks of max 10000 addresses each. In reality, this usually
    // means that all addresses for libxul get processed in one chunk, and the
    // addresses from all other libraries get processed in a second chunk.
    // The driving idea behind this is to minimize latency: If symbolication for
    // all libraries is blocked on getting symbols for libxul, latency suffers.
    // On the other hand, if we fire off a separate request for each library,
    // latency also suffers because of per-request overhead and pipelining limits.
    // We also limit each chunk to at most MAX_JOB_COUNT_PER_CHUNK libraries.
    const chunks = _partitionIntoChunksOfMaxValue(
      requests,
      10000,
      MAX_JOB_COUNT_PER_CHUNK,
      ({ addresses }) => addresses.size
    );

    // Kick off the requests to the symbolication API, and create a list of
    // promises, one promise per chunk.
    const symbolicationApiRequestsAndResponsesPerChunk: Array<
      [LibSymbolicationRequest[], Promise<LibSymbolicationResponse[]>]
    > = chunks.map((requests) => [
      requests,
      this._symbolProvider.requestSymbolsFromServer(requests),
    ]);

    // Process the results from the symbolication API request, as they arrive.
    // For unsuccessful requests, fall back to _getSymbolsFromBrowser.
    await Promise.all(
      symbolicationApiRequestsAndResponsesPerChunk.map(
        async ([chunkRequests, chunkResponsePromise]) => {
          // Store any errors encountered along the way in this map.
          // We will report them if all avenues fail.
          const errorMap: Map<LibSymbolicationRequest, Error[]> = new Map(
            requests.map((r): [LibSymbolicationRequest, Error[]] => [r, []])
          );

          // Process the results of option 1: The response from the Mozilla symbolication APi.
          const requestsRemainingAfterServerAPI =
            await this._processSuccessfulResponsesAndReturnRemaining(
              chunkRequests,
              chunkResponsePromise,
              errorMap,
              successCb
            );

          if (requestsRemainingAfterServerAPI.length === 0) {
            // Done!
            return;
          }

          // Some libraries are still remaining, try option 2 for them.
          // Option 2 is symbolProvider.requestSymbolsFromBrowser.
          const browserAPIResponsePromise =
            this._symbolProvider.requestSymbolsFromBrowser(
              requestsRemainingAfterServerAPI
            );
          const requestsRemainingAfterBrowserAPI =
            await this._processSuccessfulResponsesAndReturnRemaining(
              requestsRemainingAfterServerAPI,
              browserAPIResponsePromise,
              errorMap,
              successCb
            );

          if (requestsRemainingAfterBrowserAPI.length === 0) {
            // Done!
            return;
          }

          // Some libraries are still remaining, try option 3 for them.
          // Option 3: Request a symbol table from the browser.
          // Now we've reached the stage where we check for symbol tables.
          const responsesPromise: Promise<LibSymbolicationResponse[]> =
            Promise.all(
              requestsRemainingAfterBrowserAPI.map(async (request) => {
                try {
                  const { lib } = request;
                  const results =
                    await this._symbolProvider.requestSymbolsViaSymbolTableFromBrowser(
                      request,
                      ignoreCache
                    );
                  return { type: 'SUCCESS', lib, results };
                } catch (error) {
                  return { type: 'ERROR', request, error };
                }
              })
            );

          const failedRequests =
            await this._processSuccessfulResponsesAndReturnRemaining(
              requestsRemainingAfterBrowserAPI,
              responsesPromise,
              errorMap,
              successCb
            );

          if (failedRequests.length === 0) {
            // Done!
            return;
          }

          for (const request of failedRequests) {
            const { lib } = request;
            // None of the symbolication methods were successful.
            // Call the error callback with all accumulated errors.
            errorCb(
              request,
              new SymbolsNotFoundError(
                `Could not obtain symbols for ${lib.debugName}/${lib.breakpadId}.`,
                lib,
                ...(errorMap.get(request) ?? [])
              )
            );
          }
        }
      )
    );
  }

  // This method awaits `responsesPromise`. All successful responses are forwarded
  // to successCb.
  // Returns any requests which were not successful.
  // Mutates errorMap by adding the error that was encountered for a failed request.
  async _processSuccessfulResponsesAndReturnRemaining(
    requests: LibSymbolicationRequest[],
    responsesPromise: Promise<LibSymbolicationResponse[]>,
    errorMap: Map<LibSymbolicationRequest, Error[]>,
    successCb: (lib: RequestedLib, results: Map<number, AddressResult>) => void
  ): Promise<LibSymbolicationRequest[]> {
    try {
      const responses: LibSymbolicationResponse[] = await responsesPromise;

      const failedRequests = [];
      for (let i = 0; i < responses.length; i++) {
        const response = responses[i];
        if (response.type === 'SUCCESS') {
          // We've found symbols for this library!
          const { lib, results } = response;
          successCb(lib, results);
        } else {
          // No symbols for this library, it'll need to try the next option.
          const { request, error } = response;
          failedRequests.push(request);
          // Put the error into the errorMap.
          ensureExists(errorMap.get(request)).push(error);
        }
      }
      return failedRequests;
    } catch (error) {
      // There was a problem for the entire chunk of requests, for example there
      // might have been a problem parsing the response JSON.
      // Add this error for all requests.
      for (const request of requests) {
        ensureExists(errorMap.get(request)).push(error);
      }
      return requests;
    }
  }
}
